import React, {
  FocusEvent,
  forwardRef,
  memo,
  MouseEventHandler,
  ReactElement,
  ReactNode,
  Ref,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState
} from 'react'
import Input, { InputProps } from 'core/components/inputs/Input'
import Combobox from './Combobox'
import Tag from 'core/components/misc/Tag'

import Menu, {
  MenuProps,
  StandardOptionData
} from 'core/components/dialogs/Menu'
import { ID } from 'types/entity'
import Popover from 'core/components/dialogs/Popover'
import { buildOptionsMap } from './helpers'
import cn from 'classnames'
import Icon from '../icons/Icon'
import { IconType } from '../icons/types'

const iconTypeList: string[] = Object.values(IconType)

export interface SelectProps<T extends { token_id: ID }>
  extends Omit<InputProps, 'onChange' | 'type' | 'value' | 'children'> {
  options: MenuProps<T>['options']
  onChange?: MenuProps<T>['onChange']
  value?: MenuProps<T>['value']
  getDisplayValue?: (
    option?:
      | string
      | number
      | StandardOptionData<T>
      | ReactElement<{ value: T['token_id']; onClick: VoidFunction }>
  ) => ReactNode
  matchReferenceWidth?: boolean
  useTagOptions?: boolean
  clearable?: boolean
  dropdownExpandedIcon?: `${IconType}` | ReactElement
  menuVariant?: MenuProps<T>['variant']
  wrapperClassName?: string
  dropdownClassName?: string
}

const Select = memo(
  forwardRef(
    <T extends { token_id: ID }>(
      {
        name = 'select-input',
        options = [],
        value = '',
        onChange,
        onFocus,
        suffix = 'down',
        getDisplayValue = (v) => {
          if (!v) return null
          if (typeof v === 'string' || typeof v === 'number') return v
          if ('label' in v) return v.label
          return v.props.value
        },
        disabled,
        matchReferenceWidth = true,
        useTagOptions = false,
        readOnly = false,
        dropdownExpandedIcon = 'up',
        clearable = false,
        menuVariant = 'default',
        wrapperClassName = '',
        dropdownClassName = '',
        ...rest
      }: SelectProps<T>,
      ref: Ref<HTMLDivElement>
    ) => {
      const elementRef = useRef<HTMLDivElement>(null)
      const [menuExpanded, setMenuExpanded] = useState(false)
      const allowMultipleSelection = Array.isArray(value)
      const customId = useId()
      const hasValue = allowMultipleSelection ? value.length > 0 : !!value

      const hashMap = useMemo(() => {
        return buildOptionsMap(options)
      }, [options])

      const selectedOptions = useMemo(() => {
        if (allowMultipleSelection) return value.map((v) => hashMap.get(v) ?? v)
        return [hashMap.get(value) ?? value]
      }, [allowMultipleSelection, hashMap, value])

      const suffixEl = useMemo(() => {
        if (readOnly) return undefined

        const icon = menuExpanded
          ? dropdownExpandedIcon
          : hasValue && clearable
            ? 'close'
            : suffix

        const showCloseIcon =
          hasValue && !menuExpanded && clearable && !readOnly

        const onClick: MouseEventHandler<HTMLSpanElement> = (e) => {
          e.stopPropagation()

          setMenuExpanded(hasValue && clearable ? false : !menuExpanded)
          if (hasValue && clearable && icon === 'close')
            onChange?.(
              allowMultipleSelection
                ? ([] as Array<T['token_id'] | string | number>)
                : ''
            )
        }

        if (typeof icon === 'string' && iconTypeList.includes(icon)) {
          return (
            <Icon
              name={icon as IconType}
              width={9}
              height={9}
              className={cn(
                'text-xs p-px h-fit cursor-pointer',
                showCloseIcon && 'rounded-full bg-dark-3 hover:bg-dark-4'
              )}
              color={showCloseIcon ? 'text-white' : 'text-dark-3'}
              onClick={onClick}
              noWrapper
            />
          )
        }

        return (
          <span className="cursor-pointer text-xs text-dark-3 hover:text-dark-4">
            {icon}
          </span>
        )
      }, [
        readOnly,
        allowMultipleSelection,
        clearable,
        dropdownExpandedIcon,
        hasValue,
        menuExpanded,
        onChange,
        suffix
      ])

      const prefixEl = useMemo(() => {
        if (!rest.prefix) return null
        if (rest.prefix && (rest.prefix as string) in IconType) {
          return (
            <Icon
              name={rest.prefix as IconType}
              width={9}
              height={9}
              className={cn('text-xs p-px h-fit cursor-pointer')}
              color="text-dark-3"
              noWrapper
            />
          )
        }

        return (
          <span className="cursor-pointer text-xs text-dark-3 mr-2">
            {rest.prefix}
          </span>
        )
      }, [rest.prefix])

      const onMenuItemOnChangeCB = useCallback(
        (v: typeof value) => {
          onChange?.(v)
          setMenuExpanded(Array.isArray(v) && v.length > 0)
        },
        [onChange]
      )
      const onFocusCB = useCallback(
        (e: FocusEvent<HTMLInputElement>) => {
          onFocus?.(e)
          // setMenuExpanded(!readOnly)
        },
        [onFocus]
      )

      useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
          if (
            // @ts-ignore
            event?.target?.id?.includes('menu_scrollable_list')
          ) {
            return setMenuExpanded(true)
          }
          if (
            elementRef.current &&
            !elementRef.current.contains(event.target as Node) &&
            // @ts-ignore
            event?.target?.nodeName !== 'INPUT'
          ) {
            setMenuExpanded(false)
          }
        }

        const handleEscapeKey = (event: KeyboardEvent) => {
          if (event.key === 'Escape') {
            setMenuExpanded(false)
          }
        }

        document.addEventListener('mousedown', handleClickOutside)
        document.addEventListener('keydown', handleEscapeKey)

        return () => {
          document.removeEventListener('mousedown', handleClickOutside)
          document.removeEventListener('keydown', handleEscapeKey)
        }
      }, [setMenuExpanded])

      const optionEls = useMemo(() => {
        if (useTagOptions) {
          return options.map((opt) => {
            if (typeof opt === 'string' || typeof opt === 'number')
              return (
                <Tag key={opt} value={opt}>
                  {opt}
                </Tag>
              )
            if ('token_id' in opt)
              return (
                <Tag
                  key={opt.token_id}
                  value={opt.token_id}
                  prefix={opt.prefix}
                  suffix={opt.suffix}
                >
                  {getDisplayValue(opt) ?? opt.token_id}
                </Tag>
              )
            if ('props' in opt) return opt
            throw new Error(
              'Select cannot accept react element options or 2D options when useTagOptions=true'
            )
          })
        }
        return options
      }, [getDisplayValue, options, useTagOptions])

      const displayValue = useMemo(
        () => getDisplayValue?.(selectedOptions[0]) ?? '',
        [getDisplayValue, selectedOptions]
      )

      return (
        <div ref={ref} key={`select_${customId}`}>
          <div
            ref={elementRef}
            onClick={(e) => e.stopPropagation()}
            className={cn('inline-flex w-full', wrapperClassName)}
          >
            <Popover
              show={menuExpanded}
              position="bottom-left"
              useArrow={false}
              matchReferenceWidth={matchReferenceWidth}
              content={
                <Menu
                  className={cn(['max-h-72 min-w-fit', dropdownClassName])}
                  options={optionEls}
                  value={value}
                  onChange={onMenuItemOnChangeCB}
                  variant={menuVariant}
                />
              }
            >
              {allowMultipleSelection ? (
                <Combobox
                  onFocus={onFocusCB}
                  name={name}
                  values={selectedOptions}
                  suffix={suffixEl}
                  onChange={onMenuItemOnChangeCB}
                  //eslint-disable-next-line
                  //@ts-ignore intentional type ignoring
                  renderTag={getDisplayValue}
                  readOnly={readOnly}
                  {...rest}
                  onClick={(e: any) => {
                    e.stopPropagation()
                    rest?.onClick?.(e)
                    setMenuExpanded(!menuExpanded)
                  }}
                />
              ) : ['string', 'number'].includes(typeof displayValue) ? (
                <Input
                  name={name}
                  suffix={suffixEl}
                  disabled={disabled || readOnly}
                  value={displayValue.toString()}
                  onFocus={onFocusCB}
                  type="select"
                  {...rest}
                  onClick={(e) => {
                    e.stopPropagation()
                    rest?.onClick?.(e)
                    setMenuExpanded(!menuExpanded)
                  }}
                  readOnly
                />
              ) : (
                // @TODO: extract this prefix-suffix-slot pattern to a base component and share this with Input, Button
                <div
                  className={cn(
                    'px-4 py-2 overflow-hidden bg-white text-default rounded-md outline outline-1 focus-within:outline-primary-0 hover:outline-primary-0 outline-dark-2 flex items-center justify-between gap-2 cursor-pointer',
                    disabled &&
                      'bg-dark-1 text-dark-7 cursor-not-allowed pointer-events-none',
                    rest.className,
                    rest.variant === 'invisible' &&
                      '!outline-0 hover:outline-1 hover:!outline-primary bg-transparent'
                  )}
                  onClick={() => setMenuExpanded(true)}
                >
                  <span className="w-full">
                    {prefixEl}
                    {displayValue}
                  </span>
                  {suffixEl}
                </div>
              )}
            </Popover>
          </div>
        </div>
      )
    }
  )
)

export default Select
